import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Check,
  Circle,
  CircleAlert,
  CloudUpload,
  Loader2,
  Plug,
  RefreshCw,
} from 'lucide-react';
import {
  installServiceWorker,
  uninstallServiceWorker,
} from '@/services/swRegistration';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { describeSyncStatus } from './describeSyncStatus';
import { SyncStageKey, SyncStageStatus, requestSync } from './swBridge';
import {
  useIsOnline,
  useServiceWorkerEnabled,
  useSyncStatus,
} from './useSyncStatus';

const STAGE_LABELS: Record<SyncStageKey, string> = {
  'post-attachments': 'Uploading attachments',
  'post-ticketevents': 'Uploading ticket changes',
  'post-listevents': 'Uploading checklist changes',
  'get-entities': 'Getting projects, works, users & labels',
  'get-tickets': 'Getting tickets',
  'get-lists': 'Getting checklists',
  'get-documents': 'Getting documents of followed projects',
};

/** Radial progress ring for busy stages with known per-item totals. */
function ProgressRing({
  progress,
  total,
}: {
  progress: number;
  total: number;
}) {
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const fraction = total > 0 ? Math.min(progress / total, 1) : 0;
  return (
    <svg viewBox="0 0 16 16" className="size-4 -rotate-90 text-blue-500">
      <circle
        cx="8"
        cy="8"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        opacity="0.25"
      />
      <circle
        cx="8"
        cy="8"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - fraction)}
        className="transition-[stroke-dashoffset] duration-300"
      />
    </svg>
  );
}

function StageBullet({ stage }: { stage: SyncStageStatus }) {
  switch (stage.state) {
    case 'busy':
      // Known totals render as a filling ring; indeterminate stages spin.
      return stage.total != null && stage.total > 0 ? (
        <ProgressRing progress={stage.progress ?? 0} total={stage.total} />
      ) : (
        <Loader2 className="size-4 animate-spin text-blue-500" />
      );
    case 'done':
      return <Check className="size-4 text-green-600 dark:text-green-500" />;
    case 'failed':
      return <CircleAlert className="text-destructive size-4" />;
    default:
      return <Circle className="text-muted-foreground/40 size-4" />;
  }
}

function StageRow({ stage }: { stage: SyncStageStatus }) {
  const progress =
    stage.total != null && stage.total > 0 && stage.state !== 'pending'
      ? ` (${stage.progress ?? 0}/${stage.total})`
      : '';
  return (
    <li className="flex items-center gap-3 py-1">
      <StageBullet stage={stage} />
      <span
        className={
          stage.state === 'pending' ? 'text-muted-foreground' : undefined
        }
      >
        {STAGE_LABELS[stage.key] ?? stage.key}
        {progress}
      </span>
    </li>
  );
}

/**
 * Sync status panel (opens on the second stack from the sidebar indicator).
 * Revamp of the current release's AppStatus dialog: connectivity, sync state
 * with a manual trigger, pending offline work, and the per-stage results of
 * the running or last sync attempt.
 */
export const SyncStatusPanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const swEnabled = useServiceWorkerEnabled();
  const status = useSyncStatus();
  const online = useIsOnline();
  const intl = useIntl();
  const [busy, setBusy] = React.useState(false);
  const syncing = status?.state === 'syncing';

  const view = describeSyncStatus(status, online, (date) =>
    intl.formatRelativeTime(
      Math.round((date.getTime() - Date.now()) / 60_000),
      'minutes',
    ),
  );
  const pending = status ? status.pendingUploads + status.pendingEvents : 0;

  const handleInstall = async () => {
    setBusy(true);
    try {
      await installServiceWorker();
      requestSync();
    } catch (error) {
      toast.error('Could not install offline sync', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUninstall = async () => {
    if (
      pending > 0 &&
      !window.confirm(
        `${pending} offline change(s) have not been synced yet and will be LOST. Uninstall anyway?`,
      )
    ) {
      return;
    }
    setBusy(true);
    await uninstallServiceWorker(); // wipes, unregisters, reloads
  };

  return (
    <Panel {...panelProps}>
      <div className="space-y-6 p-8">
        {/* Connectivity */}
        <section className="space-y-1">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <Plug
              className={`size-5 ${
                online
                  ? 'text-green-600 dark:text-green-500'
                  : 'text-destructive'
                }`}
            />
            {online ? 'Online' : 'Offline'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {online
              ? 'The backend is currently reachable.'
              : 'The backend cannot be reached — changes are stored on this device and sync when you reconnect.'}
          </p>
        </section>

        <Separator />

        {/* Opt-in: not installed yet */}
        {!swEnabled && (
          <section className="space-y-3">
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <RefreshCw className="text-muted-foreground size-5" />
              Offline sync is not set up
            </h3>
            <p className="text-muted-foreground text-sm">
              Install offline sync to work without a connection on this device:
              tickets, checklists and documents of followed projects are kept in
              sync, and changes made offline upload automatically when you
              reconnect.
            </p>
            <Button onClick={handleInstall} disabled={busy || !online}>
              {busy ? 'Installing…' : 'Install offline sync'}
            </Button>
            {!online && (
              <p className="text-muted-foreground text-xs">
                An internet connection is required to install.
              </p>
            )}
          </section>
        )}

        {/* Sync state + manual trigger */}
        {swEnabled && (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <RefreshCw
                  className={`size-5 ${view.spinning ? 'animate-spin' : ''} ${view.syncColorClass}`}
                />
                {view.label}
              </h3>
              <Button
                size="sm"
                onClick={() => requestSync()}
                disabled={syncing || !online}
              >
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{view.detail}</p>
            {pending > 0 && (
              <p className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500">
                <CloudUpload className="size-4" />
                {status!.pendingUploads} upload(s) and {status!.pendingEvents}{' '}
                change(s) waiting to be synced
              </p>
            )}
            {status?.error && (
              <p className="text-destructive text-sm">
                Last attempt: {status.error}
              </p>
            )}
          </section>
        )}

        {/* Per-stage results */}
        {swEnabled && status?.stages && (
          <>
            <Separator />
            <section className="space-y-2">
              <h4 className="text-muted-foreground text-sm font-medium">
                {syncing ? 'Progress' : 'Last attempt'}
              </h4>
              <ul className="text-sm">
                {status.stages.map((stage) => (
                  <StageRow key={stage.key} stage={stage} />
                ))}
              </ul>
            </section>
          </>
        )}

        {/* Uninstall */}
        {swEnabled && (
          <>
            <Separator />
            <section className="space-y-2">
              <p className="text-muted-foreground text-sm">
                Uninstalling removes all offline data from this device
                {pending > 0 && (
                  <span className="text-amber-600 dark:text-amber-500">
                    {' '}
                    — including {pending} change(s) not yet synced
                  </span>
                )}
                .
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleUninstall}
                disabled={busy}
              >
                {busy ? 'Uninstalling…' : 'Uninstall offline sync'}
              </Button>
            </section>
          </>
        )}
      </div>
    </Panel>
  );
};
